import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getLocalized } from '../../i18n/getLocalized';
import {
  FiPlus, FiSearch, FiFileText, FiCalendar, FiLayers,
  FiMoreVertical, FiCopy, FiTrash2, FiUpload, FiX,
  FiArrowRight, FiSettings,
} from 'react-icons/fi';
import { fetchContractTemplates, createContractTemplate, duplicateContractTemplate, deleteContractTemplate } from '../../store/slices/contractTemplatesSlice';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/dateFormat';
import PageHeader from '../../components/PageHeader/PageHeader';
import './ContractTemplates.scss';

function hexToRgb(hex) {
  if (!hex) return '99, 102, 241';
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(v, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

const ACCEPT_DOCX = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// `embedded` = rendered inside the Contract Settings panel, which already
// shows this page's standard header.
function ContractTemplates({ embedded = false }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { items: templates, loading } = useSelector(s => s.contractTemplates);
  const accent = useSelector(
    (s) => s.settings?.branding?.accentColor
      || s.settings?.config?.branding?.accentColor
      || '#6366f1'
  );
  const accentRgb = useMemo(() => hexToRgb(accent), [accent]);

  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchContractTemplates({ search: search || undefined }));
  }, [dispatch, search]);

  // Close the card kebab menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = () => setMenuOpen(null);
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, [menuOpen]);

  const resetCreate = useCallback(() => {
    setShowCreate(false);
    setNewName('');
    setNewDesc('');
    setNewFile(null);
    setDragActive(false);
  }, []);

  const acceptFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error(t('contractTemplates.messages.uploadRequired'));
      return;
    }
    setNewFile(file);
  }, [t]);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) return toast.error(t('contractTemplates.messages.nameRequired'));
    if (!newFile) return toast.error(t('contractTemplates.messages.uploadRequired'));

    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('name', newName.trim());
      formData.append('description', newDesc.trim());
      formData.append('document', newFile);

      const result = await dispatch(createContractTemplate(formData)).unwrap();
      toast.success(t('contractTemplates.messages.created'));
      resetCreate();
      navigate(`/contract-templates/${result.template._id}/edit`);
    } catch (err) {
      toast.error(err || t('contractTemplates.messages.failedToCreate'));
    } finally {
      setCreating(false);
    }
  }, [dispatch, navigate, newName, newDesc, newFile, t, resetCreate]);

  const handleDuplicate = useCallback(async (id) => {
    try {
      await dispatch(duplicateContractTemplate(id)).unwrap();
      toast.success(t('contractTemplates.messages.duplicated'));
      setMenuOpen(null);
    } catch (err) {
      toast.error(err || t('contractTemplates.messages.failedToDuplicate'));
    }
  }, [dispatch, t]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm(t('contractTemplates.messages.deleteConfirm'))) return;
    try {
      await dispatch(deleteContractTemplate(id)).unwrap();
      toast.success(t('contractTemplates.messages.deleted'));
      setMenuOpen(null);
    } catch (err) {
      toast.error(err || t('contractTemplates.messages.failedToDelete'));
    }
  }, [dispatch, t]);

  const renderSkeletons = () => (
    <div className="ct-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="ct-card ct-card--skeleton" aria-hidden="true">
          <div className="ct-card__header">
            <div className="ct-shimmer ct-shimmer--icon" />
          </div>
          <div className="ct-shimmer ct-shimmer--line ct-shimmer--w-70" />
          <div className="ct-shimmer ct-shimmer--line ct-shimmer--w-90" />
          <div className="ct-card__footer">
            <div className="ct-shimmer ct-shimmer--pill" />
            <div className="ct-shimmer ct-shimmer--pill" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="ct-page" style={{ '--ct-accent': accent, '--ct-accent-rgb': accentRgb }}>
      {/* Header */}
      <header className="ct-head">
        {/* Embedded under Contract Settings, the page title is already rendered
            by the panel's standard page header — showing it again would be a
            second heading for the same page. */}
        {!embedded && (
          <PageHeader
            label={t('nav.businessConfig', 'Contract Settings')}
            labelIcon={<FiSettings />}
            title={t('contractTemplates.title')}
            description={t('pageDescriptions.templates')}
          />
        )}
        <button className="ct-btn ct-btn--primary" onClick={() => setShowCreate(true)}>
          <FiPlus size={16} />
          <span>{t('contractTemplates.createNewTemplate')}</span>
        </button>
      </header>

      {/* Search */}
      <div className="ct-search">
        <FiSearch className="ct-search__icon" />
        <input
          className="ct-search__input"
          placeholder={t('contractTemplates.searchTemplates')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="ct-search__clear" onClick={() => setSearch('')} aria-label={t('contractTemplates.cancel')}>
            <FiX size={14} />
          </button>
        )}
      </div>

      {/* Templates */}
      {loading ? (
        renderSkeletons()
      ) : templates.length === 0 ? (
        <div className="ct-empty">
          <div className="ct-empty__icon"><FiFileText size={28} /></div>
          <h3>{search ? t('contractTemplates.searchTemplates') : t('contractTemplates.noTemplatesYet')}</h3>
          <p>{t('contractTemplates.noTemplatesDesc')}</p>
          {!search && (
            <button className="ct-btn ct-btn--primary" onClick={() => setShowCreate(true)}>
              <FiPlus size={16} />
              <span>{t('contractTemplates.createNewTemplate')}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="ct-grid">
          {templates.map((tmpl, i) => (
            <div
              key={tmpl._id}
              className="ct-card"
              style={{ '--i': i }}
              onClick={() => navigate(`/contract-templates/${tmpl._id}/edit`)}
            >
              <div className="ct-card__header">
                <div className="ct-card__icon"><FiFileText size={20} /></div>
                <div className="ct-card__menu-wrap">
                  <button
                    className="ct-card__menu-btn"
                    aria-label={t('contractTemplates.edit')}
                    onClick={e => { e.stopPropagation(); setMenuOpen(menuOpen === tmpl._id ? null : tmpl._id); }}
                  >
                    <FiMoreVertical size={16} />
                  </button>
                  {menuOpen === tmpl._id && (
                    <div className="ct-card__dropdown" onClick={e => e.stopPropagation()}>
                      <button onClick={e => { e.stopPropagation(); handleDuplicate(tmpl._id); }}>
                        <FiCopy size={14} /> {t('contractTemplates.duplicate')}
                      </button>
                      <button className="ct-card__dropdown-danger" onClick={e => { e.stopPropagation(); handleDelete(tmpl._id); }}>
                        <FiTrash2 size={14} /> {t('contractTemplates.delete')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="ct-card__body">
                <h3 className="ct-card__name">{tmpl.name}</h3>
                {tmpl.description
                  ? <p className="ct-card__desc">{tmpl.description}</p>
                  : <p className="ct-card__desc ct-card__desc--empty">{t('contractTemplates.descriptionPlaceholder')}</p>}
              </div>
              <div className="ct-card__footer">
                <span className="ct-card__meta">
                  <FiLayers size={12} />
                  {t('contractTemplates.fieldsMapped', { count: tmpl.mappedFieldCount || 0 })}
                </span>
                <span className="ct-card__meta">
                  <FiCalendar size={12} />
                  {formatDate(tmpl.createdAt)}
                </span>
                {tmpl.fieldSchemaId && (
                  <span className="ct-card__schema-badge">
                    {getLocalized(tmpl.fieldSchemaId.name, i18n.language) || t('adminPanel.chips.schema')}
                  </span>
                )}
              </div>
              <span className="ct-card__open" aria-hidden="true">
                {t('contractTemplates.edit')} <FiArrowRight size={13} />
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="ct-modal-overlay" onClick={resetCreate}>
          <div className="ct-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="ct-modal__header">
              <h2>{t('contractTemplates.createNewTemplate')}</h2>
              <button className="ct-modal__close" onClick={resetCreate} aria-label={t('contractTemplates.cancel')}><FiX size={18} /></button>
            </div>
            <div className="ct-modal__body">
              <div className="ct-modal__field">
                <label>{t('contractTemplates.templateName')} <span className="ct-modal__required">*</span></label>
                <input
                  type="text"
                  placeholder="e.g., NDA Template, Service Agreement…"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="ct-modal__field">
                <label>{t('contractTemplates.description')}</label>
                <textarea
                  placeholder={t('contractTemplates.descriptionPlaceholder')}
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="ct-modal__field">
                <label>{t('contractTemplates.uploadTemplateDocument')} <span className="ct-modal__required">*</span></label>
                <div
                  className={`ct-upload ${newFile ? 'ct-upload--has-file' : ''} ${dragActive ? 'ct-upload--drag' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
                  onDrop={e => {
                    e.preventDefault();
                    setDragActive(false);
                    acceptFile(e.dataTransfer.files?.[0]);
                  }}
                >
                  {newFile ? (
                    <div className="ct-upload__file">
                      <span className="ct-upload__file-icon"><FiFileText size={18} /></span>
                      <span className="ct-upload__file-name">{newFile.name}</span>
                      <button onClick={e => { e.stopPropagation(); setNewFile(null); }} aria-label={t('contractTemplates.cancel')}><FiX size={14} /></button>
                    </div>
                  ) : (
                    <>
                      <span className="ct-upload__icon"><FiUpload size={20} /></span>
                      <span className="ct-upload__title">{t('contractTemplates.clickToUpload')}</span>
                      <span className="ct-upload__hint">{t('contractTemplates.uploadHint')}</span>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_DOCX}
                  style={{ display: 'none' }}
                  onChange={e => acceptFile(e.target.files[0] || null)}
                />
              </div>
            </div>
            <div className="ct-modal__footer">
              <button className="ct-btn ct-btn--ghost" onClick={resetCreate}>{t('contractTemplates.cancel')}</button>
              <button className="ct-btn ct-btn--primary" onClick={handleCreate} disabled={creating}>
                {creating ? t('contractTemplates.creating') : t('contractTemplates.createAndOpenEditor')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContractTemplates;
